import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import { styled } from 'styled-components';
import { FieldProps } from 'formik';
import { mdiClose } from '@mdi/js';
import { useTranslation } from 'react-i18next';

import { IFileFragment } from 'graphql/types';
import { FieldLabel } from 'common/components/FieldLabel';
import { ActionButton } from 'common/components/ActionButton';
import { Icon } from 'common/components/Icon';
import { EImageVariant } from 'common/types';

import { ImageUploader } from '../ImageUploader/ImageUploader';
import { ImageUploaderWithDefaults } from '../ImageUploader/ImageUploaderWithDefaults';

interface IProps extends FieldProps {
    className?: string;
    helperText?: string;
    label?: string;
    withDefaults?: boolean;
    maxUploadSize: number;
    acceptedFileTypes?: string;
    enableCropping?: boolean;
    imageVariant?: EImageVariant;
    onChange?(name: string, file: IFileFragment | null): void;
    onLoaded?(): void;
}

const BaseImageField = ({
    helperText,
    label,
    withDefaults,
    className,
    field,
    onChange,
    onLoaded,
    enableCropping = true,
    imageVariant,
    form: { setFieldValue },
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { value, name } = field;

    const handleChange = (newValue: IFileFragment | null) => {
        setFieldValue(name, newValue);

        onChange && onChange(name, newValue);
    };

    return (
        <Box className={className}>
            <Box sx={{ height: '150px', position: 'relative' }}>
                {!withDefaults && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 1,
                        }}
                    >
                        <ActionButton
                            aria-label={translate('removeImage')}
                            color="whiteTransparent"
                            size="medium"
                            onClick={() => handleChange(null)}
                        >
                            <Icon path={mdiClose} size="2.4rem" />
                        </ActionButton>
                    </Box>
                )}

                {!!label && (
                    <FieldLabel id="imageFieldLabel">
                        <Typography>{label}</Typography>
                    </FieldLabel>
                )}

                {withDefaults ? (
                    <ImageUploaderWithDefaults
                        aria-labelledby="imageFieldLabel"
                        enableCropping={enableCropping}
                        id={name}
                        image={value}
                        imageVariant={imageVariant}
                        onChange={handleChange}
                        onLoaded={onLoaded}
                        {...other}
                    />
                ) : (
                    <ImageUploader
                        aria-labelledby="imageFieldLabel"
                        enableCropping={enableCropping}
                        id={name}
                        image={value}
                        imageVariant={imageVariant}
                        onUpload={handleChange}
                        {...other}
                    />
                )}
            </Box>
            {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
        </Box>
    );
};

export const ImageField = styled(BaseImageField)`
    .MuiFormHelperText-root {
        font-weight: ${({ theme }) => theme.typography.fontWeightBold};
        color: ${({ theme }) => theme.vars.palette.grey[500]};
        margin-top: ${({ theme }) => theme.spacing(1)};
    }
`;
